"use client";

import { useEffect, useRef, useState } from "react";
import solves from "./solves.json";
import styles from "./SolvesChart.module.css";

// Accepted lean-eval v1 solves of the four leading teams over time, with the model releases as grey bands.
// The data comes from scripts/leaneval-solves.py; all dates are UTC.

type Point = [number, number];

const DAY = 86_400_000;
const utc = (date: string) => Date.parse(`${date}T00:00:00Z`);

// Fixed order and colours (checked for colour-blind separation and 3:1 contrast on white).
const TEAMS = [
  { id: "GenProof", legend: "GenProof · start Oct 3", label: "us", color: "#025fbc", us: true },
  { id: "Axiom", legend: "Axiom · start Aug 29", label: "Axiom", color: "#e07a2e" },
  { id: "Humanifa", legend: "Humanifa · start Aug 16", label: "Humanifa", color: "#14996b" },
  { id: "NEAR", legend: "NEAR · start Sep 11", label: "NEAR", color: "#c255a0" },
] as const;

const SERIES = TEAMS.map((t) => ({ ...t, points: (solves as unknown as Record<string, Point[]>)[t.id] }));

const RELEASES = [
  { label: "GPT-6 Astra", from: utc("2026-09-03"), to: utc("2026-09-05") },
  { label: "Opus 5.5", from: utc("2026-09-22"), to: utc("2026-09-23") },
  { label: "GPT-6.1", from: utc("2026-09-29"), to: utc("2026-09-30") },
];

const V1_PUBLISHED = utc("2026-08-20");
const X0 = utc("2026-08-14");
const X1 = utc("2026-10-24");
const X_TICKS = ["2026-08-20", "2026-09-01", "2026-09-15", "2026-10-01", "2026-10-15"].map(utc);
const Y_TICKS = [0, 25, 50, 75, 100];
// The count axis is a power scale: it gives the top of the range, where the teams finish, more height. The
// gridlines are at their true values, so the spacing shows the stretch.
const Y_POWER = 1.4;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const day = (t: number) => {
  const d = new Date(t);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

// Roboto Mono advances 0.6em per character.
const textWidth = (text: string, size: number) => text.length * size * 0.6;

function countAt(points: Point[], t: number): number | null {
  let count: number | null = null;
  for (const [time, c] of points) {
    if (time > t) break;
    count = c;
  }
  return count;
}

const ARIA =
  "Accepted lean-eval v1 solves over time. GenProof 92, from Oct 3; Axiom 90, from Aug 29; " +
  "Humanifa 89, from Aug 16; NEAR 88, from Sep 11. Model releases: GPT-6 Astra Sep 3–4, Opus 5.5 Sep 22, GPT-6.1 Sep 29.";

export default function SolvesChart() {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(752);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Geometry.
  const tick = width < 520 ? 11 : 12;
  const finals = SERIES.map((s) => s.points[s.points.length - 1]);
  const endLabels = SERIES.map((s, i) => `${s.label} ${finals[i][1]}`);
  const left = textWidth("100", tick) + 10;
  const right = Math.max(...endLabels.map((l) => textWidth(l, tick + 0.5))) + 30;
  const plotW = Math.max(width - left - right, 120);
  const plotH = Math.round(Math.min(380, Math.max(240, width * 0.48)));
  const x = (t: number) => left + ((t - X0) / (X1 - X0)) * plotW;

  // Event labels above the plot: centred on their band or line, stacked in rows when they would overlap.
  const eventFont = 11;
  const events = [{ label: "v1 published", at: V1_PUBLISHED }, ...RELEASES.map((r) => ({ label: r.label, at: (r.from + r.to) / 2 }))]
    .map((e) => {
      const w = textWidth(e.label, eventFont);
      const cx = Math.min(Math.max(x(e.at), w / 2), width - w / 2);
      return { ...e, w, cx };
    })
    .sort((a, b) => a.cx - b.cx);
  const rowEnds: number[] = [];
  const placed = events.map((e) => {
    let row = rowEnds.findIndex((end) => e.cx - e.w / 2 > end + 6);
    if (row < 0) row = rowEnds.push(0) - 1;
    rowEnds[row] = e.cx + e.w / 2;
    return { ...e, row };
  });
  const rowH = eventFont + 5;
  const top = rowEnds.length * rowH + 10;
  const bottom = 28;
  const height = top + plotH + bottom;
  const y = (v: number) => top + plotH - Math.pow(v / 100, Y_POWER) * plotH;
  const xEnd = x(X1);

  // Date ticks that have room for their label.
  const xTicks: number[] = [];
  for (const t of X_TICKS) {
    const prev = xTicks[xTicks.length - 1];
    if (prev === undefined || x(t) - x(prev) > textWidth("Aug 20", tick) + 12) xTicks.push(t);
  }

  // Lines: start at zero, step up at each accepted solve, run flat to the end.
  const path = (points: Point[]) =>
    `M${x(points[0][0])},${y(0)}` + points.map(([t, c]) => `H${x(t)}V${y(c)}`).join("") + `H${xEnd}`;
  const area = (points: Point[]) => `${path(points)}V${y(0)}H${x(points[0][0])}Z`;

  // End labels in value order, each with its series key, pushed apart where they would touch.
  const gap = tick + 4;
  const ends = SERIES.map((s, i) => ({ s, text: endLabels[i], at: y(finals[i][1]), ly: y(finals[i][1]) })).sort((a, b) => a.at - b.at);
  for (let i = 1; i < ends.length; i++) ends[i].ly = Math.max(ends[i].ly, ends[i - 1].ly + gap);
  const overflow = ends[ends.length - 1].ly - (top + plotH);
  if (overflow > 0) ends.forEach((e) => (e.ly -= overflow));

  // Hover and keyboard: a crosshair that snaps to the day (`hover` is the day's start) and reads the counts at its
  // end.
  const snap = (t: number) => Math.min(Math.max(Math.floor(t / DAY) * DAY, X0), X1 - DAY);
  const fromPointer = (e: React.PointerEvent<SVGRectElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setHover(snap(X0 + ((e.clientX - r.left) / r.width) * (X1 - X0)));
  };
  const onKey = (e: React.KeyboardEvent) => {
    const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (e.key === "Escape") setHover(null);
    if (!step) return;
    e.preventDefault();
    setHover((h) => snap((h ?? X1 - DAY) + step * DAY));
  };
  const counts = hover === null ? [] : SERIES.map((s) => countAt(s.points, hover + DAY - 1));
  const hx = hover === null ? 0 : x(hover + DAY / 2);
  const release = hover === null ? undefined : RELEASES.find((r) => hover >= r.from && hover < r.to);
  const tipLeft = hx + 196 > width ? hx - 184 : hx + 12;

  return (
    <figure className={styles.figure}>
      <div
        ref={box}
        className={styles.plot}
        tabIndex={0}
        role="group"
        aria-label="Solves by day: use the left and right arrow keys"
        onKeyDown={onKey}
        onBlur={() => setHover(null)}
      >
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ARIA}>
          {/* Grid */}
          {Y_TICKS.map((v) => (
            <g key={v}>
              <line x1={left} x2={xEnd} y1={y(v)} y2={y(v)} className={v === 0 ? styles.baseline : styles.grid} />
              <text x={left - 8} y={y(v)} dy="0.35em" textAnchor="end" className={styles.tick} fontSize={tick}>
                {v}
              </text>
            </g>
          ))}
          {xTicks.map((t) => (
            <text key={t} x={x(t)} y={top + plotH + 19} textAnchor="middle" className={styles.tick} fontSize={tick}>
              {day(t)}
            </text>
          ))}

          {/* Model releases */}
          {RELEASES.map((r) => (
            <rect key={r.label} x={x(r.from)} y={top} width={Math.max(x(r.to) - x(r.from), 4)} height={plotH} className={styles.band} />
          ))}

          {/* v1 published */}
          <line x1={x(V1_PUBLISHED)} x2={x(V1_PUBLISHED)} y1={top} y2={top + plotH} className={styles.marker} />

          {placed.map((e) => (
            <text key={e.label} x={e.cx} y={top - 7 - e.row * rowH} textAnchor="middle" className={styles.note} fontSize={eventFont}>
              {e.label}
            </text>
          ))}

          {/* Series: ours drawn last, on top */}
          <path d={area(SERIES[0].points)} fill={SERIES[0].color} className={styles.area} />
          {[...SERIES].reverse().map((s) => (
            <path key={s.id} d={path(s.points)} stroke={s.color} className={"us" in s ? styles.lineUs : styles.line} />
          ))}
          {SERIES.map((s, i) => (
            <circle key={s.id} cx={x(finals[i][0])} cy={y(finals[i][1])} r={"us" in s ? 5 : 4} fill={s.color} className={styles.dot} />
          ))}

          {/* End labels */}
          {ends.map((e) => (
            <g key={e.s.id}>
              <line x1={xEnd + 10} x2={xEnd + 22} y1={e.ly} y2={e.ly} stroke={e.s.color} className={"us" in e.s ? styles.keyUs : styles.keyLine} />
              <text x={xEnd + 28} y={e.ly} dy="0.35em" className={"us" in e.s ? styles.endUs : styles.end} fontSize={tick + 0.5}>
                {e.text}
              </text>
            </g>
          ))}

          {/* Crosshair */}
          {hover !== null && (
            <g>
              <line x1={hx} x2={hx} y1={top} y2={top + plotH} className={styles.crosshair} />
              {SERIES.map((s, i) =>
                counts[i] === null ? null : <circle key={s.id} cx={hx} cy={y(counts[i]!)} r={3.5} fill={s.color} className={styles.dot} />,
              )}
            </g>
          )}

          <rect
            x={left}
            y={top}
            width={plotW}
            height={plotH}
            fill="transparent"
            onPointerMove={fromPointer}
            onPointerDown={fromPointer}
            onPointerLeave={() => setHover(null)}
            style={{ touchAction: "pan-y" }}
          />
        </svg>

        {hover !== null && (
          <div className={styles.tip} style={{ left: Math.max(tipLeft, 0), top: top + 8 }} aria-hidden>
            <div className={styles.tipDate}>{day(hover)}</div>
            {SERIES.map((s, i) => (
              <div key={s.id} className={styles.tipRow}>
                <span className={styles.key} style={{ background: s.color }} />
                <span className={styles.tipValue}>{counts[i] ?? "–"}</span>
                <span>{s.id}</span>
              </div>
            ))}
            {release && <div className={styles.tipNote}>{release.label} released</div>}
          </div>
        )}
      </div>

      <div className={styles.legend}>
        {SERIES.map((s) => (
          <span key={s.id} className={styles.legendItem}>
            <span className={"us" in s ? styles.legendKeyUs : styles.legendKey} style={{ background: s.color }} />
            {s.legend}
          </span>
        ))}
      </div>
    </figure>
  );
}
